import React from "react";
import "./PageContent.css";
import { useNavigate } from "react-router-dom";

/**
 * PageContent component with breadcrumb, header, title, subtitle, and main content
 *
 * @param {Object} props - Component props
 * @param {Array<{label: string, path?: string}>} props.breadcrumbs - Breadcrumb items (optional)
 * @param {string} props.title - Page title
 * @param {string} props.subtitle - Page subtitle (optional)
 * @param {React.ReactNode} props.headerActions - Action buttons/elements for the header (optional)
 * @param {React.ReactNode} props.customHeader - Custom header content to replace default header (optional)
 * @param {React.ReactNode} props.children - Main content to be rendered
 * @param {string} props.className - Additional CSS classes (optional)
 * @returns {JSX.Element} The PageContent component
 */
const PageContent = ({
  breadcrumbs = [],
  title,
  subtitle,
  headerActions,
  customHeader,
  children,
}) => {
  const navigate = useNavigate();
  return (
    <div className={`page-content-layout`}>
      {/* Breadcrumb Navigation */}
      {breadcrumbs.length > 0 && (
        <nav className="page-content-layout-breadcrumb" aria-label="Breadcrumb">
          <ol className="page-content-layout-breadcrumb-list">
            {breadcrumbs.map((crumb, index) => (
              <li key={index} className="page-content-layout-breadcrumb-item">
                {crumb.path ? (
                  <button
                    type="button"
                    onClick={() => navigate(crumb.path)}
                    className="page-content-layout-breadcrumb-link"
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span className="page-content-layout-breadcrumb-current">
                    {crumb.label}
                  </span>
                )}

                {index < breadcrumbs.length - 1 && (
                  <span
                    className="page-content-layout-breadcrumb-separator"
                    aria-hidden="true"
                  >
                    /
                  </span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}

      {/* Page Header Section */}
      {customHeader ? (
        <header className="page-content-layout-header page-content-layout-header--custom">
          {customHeader}
        </header>
      ) : (
        /* Skipped entirely when there's nothing to put in it, so a page with no
           title doesn't carry the header's bottom margin as dead space. */
        (title || subtitle || headerActions) && (
          <header className="page-content-layout-header">
            <div className="page-content-layout-header-text">
              {title && <h1 className="page-content-layout-title">{title}</h1>}
              {subtitle && (
                <p className="page-content-layout-subtitle">{subtitle}</p>
              )}
            </div>
            {headerActions && (
              <div className="page-content-layout-header-actions">
                {headerActions}
              </div>
            )}
          </header>
        )
      )}

      {/* Main Content Area */}
      <main className="page-content-layout-main">{children}</main>
    </div>
  );
};

export default PageContent;
